import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FileUp, Loader2, Wand2, Save, Printer, Sparkles, FileText, Palette } from 'lucide-react';
import { ToolLayout } from '../components/ToolLayout';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { OnePagerPreview } from '../components/OnePagerPreview';
import { PreviewScaler } from '../components/PreviewScaler';
import { templates } from '../data/templates';
import { colorSchemes } from '../data/catalog';
import { printPreview } from '../lib/print';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const infographicTemplates = templates.filter((t) => t.layout.startsWith('infographic') || ['dos-donts', 'quick-reference'].includes(t.layout));

export default function PolicySummarizer() {
  const { user } = useAuth();
  const { toast } = useToast();
  const ref = useRef(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [layout, setLayout] = useState('infographic-icons');
  const [gen, setGen] = useState(false);
  const [result, setResult] = useState(null);
  const [colorScheme, setColorScheme] = useState(colorSchemes[0]);
  const [saving, setSaving] = useState(false);
  const [docId, setDocId] = useState(null);

  const build = () => ({ id: docId, title: result?.title || title, subtitle: result?.subtitle, content: result || {}, colorScheme, layout, docType: 'policy-summary', data: { sourceName: fileName || 'Pasted text', sourceText: content } });

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setExtracting(true); setFileName(file.name);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/extract-text', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setContent(r.data.text);
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
      toast({ title: 'File read', description: `${r.data.chars.toLocaleString()} characters extracted.` });
    } catch (err) { toast({ title: 'Could not read file', description: err?.response?.data?.detail || 'Try pasting text instead.', variant: 'destructive' }); }
    finally { setExtracting(false); }
  };

  const generate = async () => {
    if (!content.trim()) { toast({ title: 'Add some content first', variant: 'destructive' }); return; }
    setGen(true);
    try { const r = await api.post('/generate-policy', { content, title, layout, audience: 'staff' }); setResult(r.data.data); }
    catch (e) { toast({ title: 'Generation failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setGen(false); }
  };

  const save = async () => {
    if (!user) return; setSaving(true);
    try { const p = build(); const r = docId ? await api.put(`/onepagers/${docId}`, p) : await api.post('/onepagers', p); setDocId(r.data.id); toast({ title: 'Saved to library!' }); }
    catch { toast({ title: 'Save failed', variant: 'destructive' }); } finally { setSaving(false); }
  };

  return (
    <ToolLayout title="Policy Summarizer" subtitle="Turn a policy into a meeting-ready infographic" icon={Sparkles}>
      {!result ? (
        <div className="max-w-3xl mx-auto">
          <Card className="mb-4"><CardContent className="p-5 space-y-4">
            <div><Label className="text-sm">Title (optional)</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Hand Hygiene Policy Summary" /></div>
            <div>
              <Label className="text-sm mb-1 block">Upload a policy document</Label>
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-teal-300 rounded-xl py-8 cursor-pointer hover:bg-teal-50/50 transition-colors">
                {extracting ? <Loader2 className="w-5 h-5 animate-spin text-teal-600" /> : <FileUp className="w-5 h-5 text-teal-600" />}
                <span className="text-sm text-gray-600">{extracting ? 'Reading file...' : fileName || 'Click to upload PDF, Word (.docx), or text file'}</span>
                <input type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={onFile} />
              </label>
            </div>
            <div className="text-center text-xs text-gray-400">— or paste the policy text below —</div>
            <div><Textarea rows={8} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste your policy, procedure, or guideline text here..." className="resize-none" /></div>
            <div className="flex flex-wrap items-center gap-3">
              <div><Label className="text-xs">Infographic Style</Label><select value={layout} onChange={(e) => setLayout(e.target.value)} className="block border rounded-lg px-3 py-2 text-sm">{infographicTemplates.map((t) => <option key={t.id} value={t.layout}>{t.name}</option>)}</select></div>
              <Button onClick={generate} disabled={gen || !content.trim()} className="ml-auto h-11 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white">{gen ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Designing infographic...</> : <><Wand2 className="w-4 h-4 mr-1" /> Generate Infographic</>}</Button>
            </div>
          </CardContent></Card>
          <p className="text-center text-xs text-gray-400"><FileText className="w-3 h-3 inline mr-1" />Your document is summarized by AI into a single-page visual you can present and print.</p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-[1fr_300px] gap-6">
          <div className="border rounded-xl bg-gray-100 p-4 overflow-hidden"><div ref={ref}><PreviewScaler><OnePagerPreview onePager={build()} /></PreviewScaler></div></div>
          <div className="space-y-4">
            <Card><CardContent className="p-4 space-y-3">
              <div><Label className="text-xs">Title</Label><Input value={result.title || ''} onChange={(e) => setResult({ ...result, title: e.target.value })} /></div>
              <div><Label className="text-xs">Subtitle</Label><Input value={result.subtitle || ''} onChange={(e) => setResult({ ...result, subtitle: e.target.value })} /></div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <Label className="text-xs mb-2 block">Infographic Template</Label>
              <select value={layout} onChange={(e) => setLayout(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mb-2">{infographicTemplates.map((t) => <option key={t.id} value={t.layout}>{t.name}</option>)}</select>
              <Button variant="outline" size="sm" className="w-full" onClick={generate} disabled={gen}>{gen ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Regenerate in this style</Button>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <Label className="text-xs flex items-center gap-1 mb-2"><Palette className="w-3 h-3" /> Colors</Label>
              <div className="grid grid-cols-4 gap-2">{colorSchemes.map((c) => <button key={c.name} onClick={() => setColorScheme(c)} className={`h-8 rounded-lg border-2 ${colorScheme.name === c.name ? 'border-gray-900' : 'border-transparent'}`} style={{ backgroundColor: c.primary }} title={c.name} />)}</div>
            </CardContent></Card>
            <div className="flex flex-col gap-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline"><FileText className="w-4 h-4 mr-1" /> View Original Policy</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                  <DialogHeader><DialogTitle>Original Policy — {fileName || 'Pasted text'}</DialogTitle></DialogHeader>
                  <div className="overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap border rounded-lg p-4 bg-gray-50">{content}</div>
                </DialogContent>
              </Dialog>
              <Button variant="outline" onClick={() => printPreview(ref.current)}><Printer className="w-4 h-4 mr-1" /> Print</Button>
              <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save to Library</Button>
              <Button variant="ghost" onClick={() => setResult(null)}>Start Over</Button>
            </div>
          </div>
        </motion.div>
      )}
    </ToolLayout>
  );
}
